import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFetchCryptoHoldings, useFetchCryptoPrices, useFetchCryptoPriceChart } from '@cryptoHooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeaderSkeleton, AccountCardSkeleton } from '@/components/LoadingSkeletons';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format } from 'date-fns';

function formatCurrency(value: number, currency = 'USD') {
  if (value > 0 && value < 0.01) {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency,
      minimumFractionDigits: 4,
      maximumFractionDigits: 8
    }).format(value);
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

export default function CryptoAssetDetailPage() {
  const { symbol } = useParams<{ symbol: string }>();
  const [chartDays, setChartDays] = useState<number>(7);

  const { data: holdings = [], isLoading: holdingsLoading } = useFetchCryptoHoldings();

  // Filter holdings for this specific asset
  const assetHoldings = useMemo(
    () => holdings.filter((h) => h.symbol.toUpperCase() === symbol?.toUpperCase()),
    [holdings, symbol]
  );

  const { data: prices = [], isLoading: pricesLoading } = useFetchCryptoPrices(symbol ? [symbol] : []);
  const { data: chartData, isLoading: chartLoading } = useFetchCryptoPriceChart(symbol || '', chartDays);

  if (holdingsLoading) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-6" aria-busy="true">
          <PageHeaderSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <AccountCardSkeleton key={i} />)}
          </div>
          <AccountCardSkeleton />
        </div>
      </ErrorBoundary>
    );
  }

  if (assetHoldings.length === 0) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-4">
          <p className="text-muted-foreground">Crypto asset not found in your portfolio.</p>
          <Button asChild variant="outline">
            <Link to="/investments/crypto">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Crypto Portfolio
            </Link>
          </Button>
        </div>
      </ErrorBoundary>
    );
  }

  const priceData = prices.find((p) => p.symbol.toUpperCase() === symbol?.toUpperCase());
  const currentPrice = priceData?.price ?? 0;
  const change24h = priceData?.change_24h ?? 0;
  
  // Aggregate stats
  const totalAmount = assetHoldings.reduce((sum, h) => sum + h.amount, 0);
  const totalCostBasis = assetHoldings.reduce((sum, h) => sum + h.cost_basis, 0);
  const currentValue = totalAmount * currentPrice;
  const profitLoss = currentValue - totalCostBasis;
  const profitLossPct = totalCostBasis > 0 ? (profitLoss / totalCostBasis) * 100 : 0;
  const isProfit = profitLoss >= 0;
  const isPositive24h = change24h >= 0;

  // Most crypto holdings should share the same base currency (usually USD)
  const currency = assetHoldings[0]?.currency ?? 'USD';
  const assetName = assetHoldings[0]?.name ?? symbol;

  const formattedChartData = useMemo(() => {
    if (!chartData) return [];
    return chartData.map(([timestamp, price]) => ({
      date: new Date(timestamp),
      price,
    }));
  }, [chartData]);

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="min-h-[44px] min-w-[44px]"
            >
              <Link to="/investments/crypto">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-4">
              {priceData?.image ? (
                <img src={priceData.image} alt={assetName} className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  {symbol?.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  {assetName}
                </h1>
                <p className="text-sm text-muted-foreground sm:text-base">
                  {symbol?.toUpperCase()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Current Price</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="text-2xl font-bold">
                    {formatCurrency(currentPrice, currency)}
                  </p>
                  <div className={`flex items-center gap-1 text-sm mt-1 ${isPositive24h ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositive24h ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {isPositive24h ? '+' : ''}{change24h.toFixed(2)}% (24h)
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <p className="text-2xl font-bold">
                  {formatCurrency(currentValue, currency)}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Cost Basis</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(totalCostBasis, currency)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Profit / Loss</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className={`text-2xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                    {isProfit ? '+' : ''}{formatCurrency(profitLoss, currency)}
                  </p>
                  <p className={`text-sm mt-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                    {isProfit ? '+' : ''}{profitLossPct.toFixed(2)}%
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Market Chart */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Market Performance</CardTitle>
            <Tabs value={chartDays.toString()} onValueChange={(val) => setChartDays(Number(val))}>
              <TabsList>
                <TabsTrigger value="1">1D</TabsTrigger>
                <TabsTrigger value="7">7D</TabsTrigger>
                <TabsTrigger value="30">1M</TabsTrigger>
                <TabsTrigger value="365">1Y</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedChartData}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => chartDays <= 1 ? format(date, 'HH:mm') : format(date, 'MMM dd')}
                      minTickGap={30}
                    />
                    <YAxis 
                      domain={['auto', 'auto']} 
                      tickFormatter={(val) => {
                        if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
                        if (val > 0 && val < 0.01) return `$${Number(val.toPrecision(2))}`;
                        return `$${val.toFixed(2)}`;
                      }}
                      width={80}
                    />
                    <RechartsTooltip 
                      labelFormatter={(label: Date) => format(label, chartDays <= 1 ? 'MMM dd, HH:mm' : 'MMM dd, yyyy')}
                      formatter={(val: number) => [formatCurrency(val, currency), 'Price']}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="price" 
                      stroke="hsl(var(--primary))" 
                      fillOpacity={1} 
                      fill="url(#colorPrice)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Wallets Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Wallets Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {assetHoldings.map((holding) => (
                <div key={holding.id} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0">
                  <div>
                    <p className="font-medium">{holding.wallet?.name ?? 'Unknown Wallet'}</p>
                    <p className="text-sm text-muted-foreground">{holding.wallet?.provider_name ?? '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">
                      {holding.amount.toLocaleString(undefined, { maximumFractionDigits: 8 })} {symbol?.toUpperCase()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(holding.amount * currentPrice, currency)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div>
          <Button asChild variant="outline">
            <Link to="/investments/crypto">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Crypto Portfolio
            </Link>
          </Button>
        </div>
      </div>
    </ErrorBoundary>
  );
}
